import React, { FC, useMemo } from "react";
import Image from "next/image";
import { Summary } from "v3/components/orderDetails/OrderSummary/Summary";
import { LineItemList } from "v3/components/orderDetails/LineItemList";
import { GiftCardFragment, PriceFragment } from "v3/services/graphql/generated";
// this is v2 because we are only using one tenant query
// and it needs to be v2 in getInitialProps where it is initially fetched
// once v2 is no longer used then this can be on v3
import { TenantQuery } from "services/graphql/generated";
import { ItemErrorMsg, ItemWithErrorMsgs } from "@pepdirect/v3/types";
import { calculateTotalNumberOfItems } from "v3/helpers/price";
import { getCloudflareLoader } from "@pepdirect/helpers/cloudflareImages";
import { endpoints } from "config";
import {
  StyledImageWrapper,
  StyledLoyaltyPointsEarned,
  StyledSummaryAndItems,
} from "./index.styled";

interface SummaryAndItemsProps {
  isSubmittedOrder?: boolean;
  itemErrors?: ItemWithErrorMsgs[];
  loyaltyError?: ItemErrorMsg | null;
  price: PriceFragment;
  tenant: TenantQuery["tenant"];
  isWithinPortal?: boolean;
  giftCard?: GiftCardFragment | null;
}

export const SummaryAndItems: FC<SummaryAndItemsProps> = ({
  isSubmittedOrder,
  itemErrors,
  loyaltyError,
  price,
  tenant,
  isWithinPortal,
  giftCard,
}) => {
  const totalNumberOfItems = useMemo(() => {
    return price?.lineItems
      ? calculateTotalNumberOfItems(price?.lineItems || [])
      : 0;
  }, [price]);

  const { loyaltyData } = price;

  const loyaltyIcon = useMemo(() => {
    return tenant?.brandConfig.customizations.images.find(
      (image) => image.name === "loyaltyIconUrl"
    );
  }, [tenant]);

  const hideTaxLine = !!tenant?.checkout?.vatInclusivePricing;

  return (
    <StyledSummaryAndItems showBorder={isWithinPortal}>
      <Summary
        discount={
          price.promoCodeDiscounts?.length ? price.promoCodeDiscounts[0] : null
        }
        giftCard={giftCard}
        hideTaxLine={hideTaxLine}
        loyaltyData={price.loyaltyData}
        loyaltyError={loyaltyError}
        quantity={totalNumberOfItems}
        regulatoryTaxTypes={price.regulatoryTaxTypes}
        shipping={price.shipping}
        subtotal={price.subtotal}
        tax={price.taxSalesTotal}
        taxRegulatoryTotal={price.taxRegulatoryTotal || undefined}
        total={price.total}
        showYourOrderText={isWithinPortal}
      />
      <LineItemList lineItems={price.lineItems} itemErrors={itemErrors} />
      {!!loyaltyData?.totalEarnedPoints && (
        <StyledLoyaltyPointsEarned>
          {isSubmittedOrder ? "You earned" : "You will earn"}
          <StyledImageWrapper>
            <Image
              src={loyaltyIcon?.src || ""}
              alt={loyaltyIcon?.alt || "loyalty icon"}
              fill
              loader={getCloudflareLoader(endpoints.cdn)}
              // double largest width for retina screens
              sizes="30px"
              style={{
                objectFit: "contain",
              }}
            />
          </StyledImageWrapper>
          {`${loyaltyData.totalEarnedPoints} points for this purchase!`}
        </StyledLoyaltyPointsEarned>
      )}
    </StyledSummaryAndItems>
  );
};
